// src/Containers/Produit/Offre.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "../../Style/SubOption.css";
import "../../Style/Accueil.ObsidianAura.css";

const SIGNUP_URL = "https://be-lifer.com/signup";
const DEMO_URL = "https://be-lifer.com/product";

export default function Offre() {
  const { t, i18n } = useTranslation("offre");

  const offers = useMemo(() => {
    const raw = t("offers.items", { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const explainerCols = useMemo(() => {
    const raw = t("credits_explainer.columns", { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const explainerAnchors = useMemo(() => {
    const raw = t("credits_explainer.anchors", { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const packs = useMemo(() => {
    const raw = t("packs.items", { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const faqItems = useMemo(() => {
    const raw = t("faq.items", { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (idx) => setOpenFaq((cur) => (cur === idx ? null : idx));

  return (
    <main className="life-home offer">
      <div className="hiw-bg" />
      <div className="life-home__overlay" />

      {/* HERO */}
      <section className="life-section offer-hero">
        <div className="life-container">
          <div className="life-glass offer-heroCard">
            <div className="offer-eyebrow">{t("hero.eyebrow")}</div>
            <h1 className="life-h1">{t("hero.title")}</h1>
            <p className="life-lead">
              {t("hero.subtitle_pre")}{" "}
              <span
                className="offer-creditTip"
                tabIndex={0}
                aria-describedby="offer-credit-def"
                title={t("hero.credit_tooltip")}
              >
                {t("hero.credit_word")}
              </span>{" "}
              {t("hero.subtitle_post")}
            </p>
            <span id="offer-credit-def" className="visually-hidden">
              {t("hero.credit_tooltip")}
            </span>

            <div className="life-ctaRow">
              <a className="life-ctaPrimary" href={SIGNUP_URL}>
                {t("hero.cta_primary")}
              </a>
              <a className="hiw-ctaSecondary" href={DEMO_URL}>
                {t("hero.cta_secondary")}
              </a>
            </div>

            {!!t("hero.reassurance") && (
              <p className="life-ctaMeta">{t("hero.reassurance")}</p>
            )}
          </div>
        </div>
      </section>

      {/* OFFERS */}
      <section className="life-section offer-offers" aria-labelledby="offers-title">
        <div className="life-container">
          <div className="offer-sectionHeader">
            <h2 id="offers-title" className="hiw-h2">{t("offers.title")}</h2>
            <p className="hiw-p muted">{t("offers.subtitle")}</p>
          </div>

          <div className="offer-grid">
            {offers.map((o) => (
              <article
                key={o.id}
                className={`life-glass offer-card ${o.featured ? "isFeatured" : ""}`}
                aria-label={o.name}
              >
                {o.featured && (
                  <div className="offer-featuredTag offer-featuredTag--top">
                    {t("offers.featured_tag")}
                  </div>
                )}

                <div className="offer-cardHeader">
                  <div className="offer-offerName">{o.name}</div>
                  {!!o.tagline && (
                    <div className="offer-caption">{o.tagline}</div>
                  )}
                  <div className="offer-offerPrice">
                    <span className="offer-priceValue">{o.price}</span>
                    {!!o.unit && <span className="offer-priceUnit">{o.unit}</span>}
                  </div>
                </div>

                <div className="offer-creditsBox">
                  <span className="offer-creditsValue">{o.credits}</span>
                </div>
                {!!o.model && <p className="offer-model">{o.model}</p>}

                <ul className="offer-list" aria-label={t("offers.a11y_list")}>
                  {(o.features || []).map((f, idx) => (
                    <li key={idx}>{f}</li>
                  ))}
                </ul>

                <div className="offer-cta">
                  <a
                    className={o.featured ? "life-ctaPrimary" : "hiw-ctaSecondary"}
                    href={SIGNUP_URL}
                  >
                    {o.cta}
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CREDITS EXPLAINER */}
      <section
        className="life-section offer-credits"
        aria-labelledby="credits-title"
      >
        <div className="life-container">
          <div className="offer-sectionHeader">
            <h2 id="credits-title" className="hiw-h2">
              {t("credits_explainer.title")}
            </h2>
            <p className="hiw-p muted">{t("credits_explainer.subtitle")}</p>
          </div>

          <div className="offer-creditsGrid">
            {explainerCols.map((c, idx) => (
              <div key={idx} className="life-glass offer-creditsCol">
                <h3 className="offer-creditsColTitle">{c.title}</h3>
                <p className="offer-creditsColBody">{c.body}</p>
              </div>
            ))}
          </div>

          <div
            className="offer-creditsAnchor"
            role="note"
            aria-label={t("credits_explainer.anchors_title")}
          >
            <div className="offer-creditsAnchorTitle">
              {t("credits_explainer.anchors_title")}
            </div>
            <ul>
              {explainerAnchors.map((a, idx) => (
                <li key={idx}>{a}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PACKS */}
      <section className="life-section offer-packs" aria-labelledby="packs-title">
        <div className="life-container">
          <div className="offer-sectionHeader">
            <h2 id="packs-title" className="hiw-h2">{t("packs.title")}</h2>
            <p className="hiw-p muted">{t("packs.subtitle")}</p>
          </div>

          <div className="offer-packsGrid">
            {packs.map((p) => (
              <div
                key={p.id}
                className={`life-glass offer-packCard ${p.tag ? "isFeatured" : ""}`}
              >
                {!!p.tag && <span className="offer-packTag">{p.tag}</span>}
                <div className="offer-packCredits">{p.credits}</div>
                <div className="offer-packPrice">{p.price}</div>
              </div>
            ))}
          </div>

          {!!t("packs.footnote") && (
            <p className="offer-packsFoot">{t("packs.footnote")}</p>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="life-section offer-faq" aria-labelledby="faq-title">
        <div className="life-container">
          <div className="offer-sectionHeader">
            <h2 id="faq-title" className="hiw-h2">{t("faq.title")}</h2>
          </div>

          <div className="offer-faqList">
            {faqItems.map((item, idx) => {
              const isOpen = openFaq === idx;
              const panelId = `faq-panel-${idx}`;
              const buttonId = `faq-button-${idx}`;
              return (
                <div
                  key={idx}
                  className={`life-glass offer-faqItem ${isOpen ? "isOpen" : ""}`}
                >
                  <button
                    type="button"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="offer-faqQ"
                    onClick={() => toggleFaq(idx)}
                  >
                    <span>{item.q}</span>
                    <span className="offer-faqChevron" aria-hidden="true" />
                  </button>
                  {isOpen && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className="offer-faqA"
                    >
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="life-section offer-ctaBanner">
        <div className="life-container">
          <div className="life-glass offer-ctaCard">
            <div className="offer-ctaText">
              <h2 className="hiw-h2">{t("cta.title")}</h2>
              <p className="hiw-p muted">{t("cta.subtitle")}</p>
            </div>
            <div className="life-ctaRow">
              <a className="life-ctaPrimary" href={SIGNUP_URL}>
                {t("cta.cta_primary")}
              </a>
              <a className="hiw-ctaSecondary" href={DEMO_URL}>
                {t("cta.cta_secondary")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}